import { useState } from 'react';
import { useToast } from '../../../shared/components/Toast';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useClientList } from '../../sp-client-management/hooks/useClientList';
import { useClients } from '@/features/client-relationships';
import { useSpMessageLaunch } from '@/features/messaging/hooks/useSpMessageLaunch';
import {
  useAddToNetwork,
  useRemoveFromNetwork,
} from '../../network/hooks/useNetwork';
import {
  useAddFavoriteProfessional,
  useRemoveFavoriteProfessional,
} from '../../favorites/hooks/useFavorites';

type ProviderDetailsActionsProps = {
  providerUserId: string;
  /** service_providers.id — used to build the serviceProviderId message deep link (spec §3.1). */
  serviceProviderId: string;
  /** Display name shown in the pre-chat intent modal, when it opens (spec §3.3). */
  providerName: string;
  professionalUserId: string;
  currentNetworkState: boolean;
  currentFavoriteState: boolean;
  canAddToNetwork: boolean;
  canFavorite: boolean;
  onNetworkAdded?: () => void; // Feature 047: Callback when provider added to network
  profileViewEngagementId?: string | null;
};

/**
 * Custom hook to manage provider details page actions
 * Handles network, favorites, messaging, scheduling, and sharing
 */
export const useProviderDetailsActions = ({
  providerUserId,
  serviceProviderId,
  providerName,
  professionalUserId,
  currentNetworkState,
  currentFavoriteState,
  canAddToNetwork,
  canFavorite,
  onNetworkAdded,
  profileViewEngagementId,
}: ProviderDetailsActionsProps) => {
  const { showToast } = useToast();
  const { launch: launchMessage, modal: messageModal } = useSpMessageLaunch({
    serviceProviderId,
    providerName,
    sourceSurface: 'full_profile',
    profileViewEngagementId: profileViewEngagementId ?? undefined,
  });
  const { user } = useAuth();
  const addToNetworkMutation = useAddToNetwork();
  const removeFromNetworkMutation = useRemoveFromNetwork();
  const addFavoriteMutation = useAddFavoriteProfessional();
  const removeFavoriteMutation = useRemoveFavoriteProfessional();

  // Local state to track optimistic updates
  const [localNetworkState, setLocalNetworkState] = useState<boolean | null>(null);
  const [localFavoriteState, setLocalFavoriteState] = useState<boolean | null>(null);

  // Feature 047: Fetch clients based on role
  const isSP = user?.role === 'SERVICE_PROVIDER';
  const isFA = user?.role === 'FINANCIAL_ADVISOR';

  const { data: spClientsData } = useClientList(
    user?.id || '',
    { limit: 1, enabled: isSP }
  );

  const { data: faClientsData } = useClients(undefined, isFA);

  const hasClients = isSP
    ? (spClientsData?.meta?.total || 0) > 0
    : isFA
      ? (faClientsData?.data?.length || 0) > 0
      : false;

  // Use local state if set, otherwise use fetched data
  const effectiveNetworkState = localNetworkState !== null ? localNetworkState : currentNetworkState;
  const effectiveFavoriteState = localFavoriteState !== null ? localFavoriteState : currentFavoriteState;

  const handleNetworkToggle = async () => {
    if (!canAddToNetwork) return;

    const wasInNetwork = effectiveNetworkState;
    const newState = !effectiveNetworkState;
    setLocalNetworkState(newState);

    try {
      if (effectiveNetworkState) {
        await removeFromNetworkMutation.mutateAsync(providerUserId);
      } else {
        await addToNetworkMutation.mutateAsync(providerUserId);

        // Feature 047: Trigger callback if just added and has clients
        if (!wasInNetwork && hasClients && onNetworkAdded) {
          onNetworkAdded();
        }
      }
    } catch (error) {
      setLocalNetworkState(null);
      console.error('Failed to update network:', error);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!canFavorite) return;
    
    const newState = !effectiveFavoriteState;
    setLocalFavoriteState(newState);

    try {
      if (effectiveFavoriteState) {
        await removeFavoriteMutation.mutateAsync(professionalUserId);
      } else {
        await addFavoriteMutation.mutateAsync({ professionalUserId });
      }
    } catch (error) {
      setLocalFavoriteState(null);
      console.error('Failed to update favorite:', error);
    }
  };

  const handleMessageProvider = () => {
    void launchMessage();
  };

  const handleShareProvider = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Profile link copied to clipboard!', 'success');
    } catch (error) {
      showToast('Failed to copy link. Please try again.', 'error');
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return {
    effectiveNetworkState,
    effectiveFavoriteState,
    handleNetworkToggle,
    handleFavoriteToggle,
    handleMessageProvider,
    handleShareProvider,
    messageModal,
    isNetworkLoading: addToNetworkMutation.isPending || removeFromNetworkMutation.isPending,
    isFavoriteLoading: addFavoriteMutation.isPending || removeFavoriteMutation.isPending,
  };
};

